import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function Footer() {
  const { t, lang } = useLang();
  const isCasa = useLocation().pathname.startsWith("/casa-de-leon");
  const brandName = isCasa ? "Casa de León" : "Punto Nima";
  const exploreIds = isCasa
    ? ["residence", "gallery", "rooms", "amenities", "guide", "reviews", "location", "booking"]
    : ["residence", "gallery", "amenities", "location", "policies", "booking"];
  const [form, setForm] = useState({ name: "", email: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error(t("Please share your name and email.")); return; }
    setSending(true);
    try {
      await api.post("/private-collection", { ...form, lang });
      toast.success(t("Welcome — you're on the Private Collection list."));
      setForm({ name: "", email: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const inputCls = "w-full bg-transparent border-0 border-b border-white/25 px-0 py-3 text-[#F6F4F0] placeholder-[#A3A09A]/70 font-light focus:outline-none focus:border-[#B86B52] transition-colors";

  return (
    <footer data-testid="footer" className="bg-[#0F2537] text-[#F6F4F0] px-6 lg:px-12 py-20">
      {/* Join the Private Collection */}
      <div data-testid="private-collection" className="max-w-[1400px] mx-auto pb-16 mb-16 border-b border-white/10 grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
        <div>
          <span className="overline text-[#B86B52]">{t("By Invitation")}</span>
          <h3 className="font-display font-light text-3xl lg:text-4xl mt-4 leading-snug">{t("Join the Private Collection")}</h3>
          <p className="text-[#A3A09A] font-light mt-4 leading-relaxed max-w-md">
            {t("A quiet list for discerning travellers — early access to new residences, seasonal openings and privately held rates. Reserved for the few.")}
          </p>
        </div>
        <form onSubmit={submit} data-testid="private-collection-form" className="space-y-8">
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            <input data-testid="pc-name" placeholder={t("Full name")} className={inputCls}
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="pc-email" type="email" placeholder={t("Email address")} className={inputCls}
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <button type="submit" data-testid="pc-submit" disabled={sending}
            className="group inline-flex items-center gap-2.5 px-8 py-3.5 border border-[#B86B52] text-[#F6F4F0] text-sm tracking-wide rounded-sm hover:bg-[#B86B52] transition-colors disabled:opacity-60">
            {sending ? t("Requesting…") : t("Request an Invitation")}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
          </button>
        </form>
      </div>

      <div className="max-w-[1400px] mx-auto grid md:grid-cols-3 gap-12">
        <div>
          <div className="font-display text-3xl mb-4">{brandName}</div>
          <p className="text-[#A3A09A] font-light leading-relaxed max-w-xs">
            {isCasa
              ? t("A hillside Tuscan villa at Punta Arena · El Tezal, Cabo San Lucas, Baja California Sur.")
              : t("A two-bedroom coastal residence at Punto Nima Resort, San José del Cabo, Baja California Sur.")}
          </p>
        </div>
        <div>
          <div className="overline text-[#A3A09A] mb-5">{t("Explore")}</div>
          <ul className="space-y-3 text-[#F6F4F0]/90 font-light">
            {exploreIds.map((id) => (
              <li key={id}>
                <button onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
                  className="capitalize hover:text-[#B86B52] transition-colors">{t(id)}</button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="overline text-[#A3A09A] mb-5">{t("Contact")}</div>
          <p className="text-[#F6F4F0]/90 font-light leading-relaxed">
            {isCasa
              ? <>Punta Arena · Cabo San Lucas<br />B.C.S. México<br /></>
              : <>Punto Nima Resort<br />San José del Cabo, B.C.S. México<br /></>}
            <span className="text-[#B86B52]">{t("Reserve directly — no platform fees")}</span>
          </p>
          <Link to="/admin/login" data-testid="footer-owner-login"
            className="inline-block mt-6 text-xs text-[#A3A09A] hover:text-[#F6F4F0] transition-colors underline underline-offset-4">
            {t("Owner Login")}
          </Link>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto mt-16 pt-8 border-t border-white/10 text-[#A3A09A] text-sm flex flex-wrap items-center justify-between gap-3">
        <span data-testid="footer-copyright">© {new Date().getFullYear()} Derron Luxury. {t("All rights reserved.")}</span>
        <Link to="/" data-testid="footer-derron-link" className="hover:text-[#F6F4F0] transition-colors tracking-[0.15em] font-display">
          A DERRON LUXURY PROPERTY
        </Link>
      </div>
    </footer>
  );
}
